import type { FunnelWizardState } from "@/lib/funnel-planner/schema";
import {
  migrateFunnelWizardState,
  normalizeFunnelWizardState,
} from "@/lib/funnel-planner/migrations";
import {
  toPersistablePlannerState,
  type PersistablePlannerState,
} from "@/lib/funnel-planner/session-fields";

export const SHARE_PARAM = "plan";
export const STEP_PARAM = "step";
export const NODE_PARAM = "node";
export const SHARE_VERSION = 2;

type SharedPlannerPayload = {
  v: 1 | typeof SHARE_VERSION;
  state: unknown;
};

export type PlannerDeepLink = {
  step: FunnelWizardState["step"] | null;
  nodeId: string | null;
};

export type SharedPlannerStateResult =
  | { state: FunnelWizardState; source: "share-url"; nodeId: string | null }
  | { state: null; source: "invalid-share-url" | "none" };

type BufferLike = {
  from(input: string | Uint8Array, encoding?: string): { toString(encoding?: string): string };
};

type Base64Globals = typeof globalThis & {
  Buffer?: BufferLike;
  atob?: (data: string) => string;
  btoa?: (data: string) => string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function normalizePlannerStep(value: unknown): FunnelWizardState["step"] | null {
  const parsed = typeof value === "number"
    ? value
    : Number.parseInt(typeof value === "string" ? value : "", 10);
  return parsed >= 1 && parsed <= 7 ? (parsed as FunnelWizardState["step"]) : null;
}

export function normalizePlannerNodeId(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return /^[a-zA-Z0-9_-]{1,80}$/.test(trimmed) ? trimmed : null;
}

function encodeBase64Url(value: string): string {
  const globals = globalThis as Base64Globals;
  if (globals.Buffer?.from) {
    return globals.Buffer.from(value, "utf8")
      .toString("base64")
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/g, "");
  }

  const bytes = new TextEncoder().encode(value);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return globals.btoa?.(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "") ?? "";
}

function decodeBase64Url(value: string): string | null {
  try {
    const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
    const globals = globalThis as Base64Globals;
    if (globals.Buffer?.from) {
      return globals.Buffer.from(padded, "base64").toString("utf8");
    }

    const binary = globals.atob?.(padded);
    if (!binary) return null;
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

/**
 * A share payload carries neither the contact nor any v7 session-only field, so
 * an existing link keeps working and a new one never transports planning
 * provenance, an adviser decision, or a seasonal assumption.
 */
export function createShareablePlannerState(state: FunnelWizardState): PersistablePlannerState {
  const normalized = normalizeFunnelWizardState(state);
  return {
    ...toPersistablePlannerState(normalized),
    contact: null,
    completedAt: null,
  };
}

export function encodePlannerShareState(state: FunnelWizardState): string {
  const payload: SharedPlannerPayload = {
    v: SHARE_VERSION,
    state: createShareablePlannerState(state),
  };
  return encodeBase64Url(JSON.stringify(payload));
}

export function decodePlannerShareState(
  encoded: string | null | undefined,
  options: { step?: unknown; nodeId?: unknown } = {},
): SharedPlannerStateResult {
  if (!encoded) return { state: null, source: "none" };

  const decoded = decodeBase64Url(encoded);
  if (!decoded) return { state: null, source: "invalid-share-url" };

  try {
    const payload = JSON.parse(decoded) as Partial<SharedPlannerPayload>;
    if (payload.v !== SHARE_VERSION && payload.v !== 1) {
      return { state: null, source: "invalid-share-url" };
    }
    if (!isRecord(payload.state)) {
      return { state: null, source: "invalid-share-url" };
    }

    const stepOverride = normalizePlannerStep(options.step);
    const nodeId = normalizePlannerNodeId(options.nodeId);
    const stateCandidate = {
      ...payload.state,
      ...(stepOverride ? { step: stepOverride } : {}),
      contact: null,
      completedAt: null,
    };
    const migrated = migrateFunnelWizardState(stateCandidate);
    return migrated
      ? { state: migrated, source: "share-url", nodeId }
      : { state: null, source: "invalid-share-url" };
  } catch {
    return { state: null, source: "invalid-share-url" };
  }
}

export function parsePlannerShareParams(params: URLSearchParams): SharedPlannerStateResult {
  return decodePlannerShareState(params.get(SHARE_PARAM), {
    step: params.get(STEP_PARAM),
    nodeId: params.get(NODE_PARAM),
  });
}
